import React, { useState, useEffect, useCallback, useRef, useMemo, Fragment } from "react";
import { useSearchParams } from "react-router-dom";
import {
  RefreshCw, Download, Eye, Search, X, Check,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight,
  ShoppingCart, TrendingUp, DollarSign,
  Filter, Loader2, Calendar,
} from "lucide-react";
import toast from "react-hot-toast";
import AdminLayout from "../../components/Admin/Layout/AdminLayout";
import apiClient from "../../services/api/client";
import { ENDPOINTS } from "../../services/api/endpoints";
import StatusBadge from "../../components/Admin/SalesOverview/StatusBadge";
import OrderDetailPanel from "../../components/Admin/SalesOverview/OrderDetailPanel";
import { useSeller } from "../../context/SellerContext";
import MarketplaceSelector from "../../components/shared/MarketplaceSelector";

const ORDER_STATUS_LIST = ["Pending", "Unshipped", "PartiallyShipped", "Shipped", "Delivered", "Canceled", "Unfulfillable"];
const FULFILLMENT_CHANNEL_LIST = ["AFN", "MFN"];
const PAYMENT_STATUS_LIST = ["COD", "Other"];
const PAGE_SIZES = [25, 50, 100];

// ── Helpers ──────────────────────────────────────────────────────────────────

const platformBadge = {
  AMAZON: "bg-orange-100 text-orange-700",
  FLIPKART: "bg-[#FFE8C8] text-[#C45500]",
  MEESHO: "bg-pink-100 text-pink-700",
  SHOPIFY: "bg-green-100 text-green-700",
};

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n);

function formatDate(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function formatDateShort(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

function timeAgo(dateStr) {
  if (!dateStr) return "";
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  if (diffMs < 0) {
    // Future date
    const futureMins = Math.floor(-diffMs / 60000);
    if (futureMins < 60) return `in ${futureMins}m`;
    const futureHrs = Math.floor(futureMins / 60);
    if (futureHrs < 24) return `in ${futureHrs}h`;
    const futureDays = Math.floor(futureHrs / 24);
    return `in ${futureDays}d`;
  }
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 30) return `${diffDays}d ago`;
  return `${Math.floor(diffDays / 30)}mo ago`;
}

function todayStr() {
  return new Date().toISOString().split("T")[0];
}

function toDateStr(d) {
  return d.toISOString().split("T")[0];
}

function daysAgoStr(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return toDateStr(d);
}

function firstOfMonth(d = new Date()) {
  return toDateStr(new Date(d.getFullYear(), d.getMonth(), 1));
}

function lastOfMonth(d = new Date()) {
  return toDateStr(new Date(d.getFullYear(), d.getMonth() + 1, 0));
}

function firstOfLastMonth() {
  const d = new Date();
  return toDateStr(new Date(d.getFullYear(), d.getMonth() - 1, 1));
}

function lastOfLastMonth() {
  return toDateStr(new Date(new Date().getFullYear(), new Date().getMonth(), 0));
}

// ── Date Presets ─────────────────────────────────────────────────────────────

const DATE_PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "Last 7 Days" },
  { key: "30d", label: "Last 30 Days" },
  { key: "this_month", label: "This Month" },
  { key: "last_month", label: "Last Month" },
  { key: "custom", label: "Custom" },
];

function getPresetDates(preset) {
  const today = todayStr();
  switch (preset) {
    case "today": return { from: today, to: today };
    case "yesterday": return { from: daysAgoStr(1), to: daysAgoStr(1) };
    case "7d": return { from: daysAgoStr(6), to: today };
    case "30d": return { from: daysAgoStr(29), to: today };
    case "this_month": return { from: firstOfMonth(), to: today };
    case "last_month": return { from: firstOfLastMonth(), to: lastOfLastMonth() };
    default: return null;
  }
}

function detectPreset(from, to) {
  for (const p of DATE_PRESETS) {
    if (p.key === "custom") continue;
    const dates = getPresetDates(p.key);
    if (dates && dates.from === from && dates.to === to) return p.key;
  }
  return "custom";
}

// ── Component ────────────────────────────────────────────────────────────────

export default function SalesOverviewPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const getParam = (key, defaultVal = "") => searchParams.get(key) || defaultVal;

  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncDays, setSyncDays] = useState("30");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const { selectedSeller: sellerCtx } = useSeller();
  const sellerId = sellerCtx?.id || "";
  const sellerChannels = useMemo(() =>
    (sellerCtx?.channels || []).filter(c => c.accountStatus === 'AUTHORIZED'), [sellerCtx]);
  const [selectedAccountId, setSelectedAccountId] = useState("");

  // Filter state
  const [search, setSearch] = useState(getParam("search"));
  const [orderStatus, setOrderStatus] = useState(getParam("orderStatus"));
  const [fulfillmentChannel, setFulfillmentChannel] = useState(getParam("fulfillmentChannel"));
  const [dateFrom, setDateFrom] = useState(getParam("dateFrom", todayStr()));
  const [dateTo, setDateTo] = useState(getParam("dateTo", todayStr()));
  const [paymentStatus, setPaymentStatus] = useState(getParam("paymentStatus"));
  const [minAmount, setMinAmount] = useState(getParam("minAmount"));
  const [maxAmount, setMaxAmount] = useState(getParam("maxAmount"));
  const [page, setPage] = useState(parseInt(getParam("page", "0")));
  const [size, setSize] = useState(parseInt(getParam("size", "25")));
  const [sortBy, setSortBy] = useState(getParam("sortBy", "purchaseDate"));
  const [sortDir, setSortDir] = useState(getParam("sortDir", "desc"));

  // Custom date range picker state
  const [showCustomRange, setShowCustomRange] = useState(false);
  const [customFrom, setCustomFrom] = useState(dateFrom);
  const [customTo, setCustomTo] = useState(dateTo);
  // Tracks whether user explicitly chose "custom" — prevents auto-snapping to a preset
  const [forceCustom, setForceCustom] = useState(false);

  const searchTimeoutRef = useRef(null);

  const activePreset = forceCustom ? "custom" : detectPreset(dateFrom, dateTo);
  const isCustomDateValid = customFrom && customTo && customFrom <= customTo;

  // Sync URL params
  useEffect(() => {
    const params = {};
    if (search) params.search = search;
    if (orderStatus) params.orderStatus = orderStatus;
    if (fulfillmentChannel) params.fulfillmentChannel = fulfillmentChannel;
    if (activePreset !== "today") {
      params.dateFrom = dateFrom;
      params.dateTo = dateTo;
    }
    if (paymentStatus) params.paymentStatus = paymentStatus;
    if (minAmount) params.minAmount = minAmount;
    if (maxAmount) params.maxAmount = maxAmount;
    if (page > 0) params.page = String(page);
    if (size !== 25) params.size = String(size);
    if (sortBy !== "purchaseDate") params.sortBy = sortBy;
    if (sortDir !== "desc") params.sortDir = sortDir;
    setSearchParams(params, { replace: true });
  }, [search, orderStatus, fulfillmentChannel, dateFrom, dateTo, paymentStatus, minAmount, maxAmount, page, size, sortBy, sortDir, setSearchParams, activePreset]);

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (sellerId) params.append("sellerId", sellerId);
      if (selectedAccountId) params.append("sellerAccountId", selectedAccountId);
      if (orderStatus) params.append("orderStatus", orderStatus);
      if (fulfillmentChannel) params.append("fulfillmentChannel", fulfillmentChannel);
      if (search) params.append("search", search);
      if (dateFrom) params.append("dateFrom", dateFrom);
      if (dateTo) params.append("dateTo", dateTo);
      params.append("page", String(page));
      params.append("size", String(size));
      const queryStr = params.toString();
      const res = await apiClient.get(`${ENDPOINTS.ORDERS.GET_ALL}?${queryStr}`);
      const data = res.data || res;
      setPageData(data);
    } catch {
      toast.error("Failed to load orders");
      setPageData(null);
    } finally {
      setLoading(false);
    }
  }, [sellerId, selectedAccountId, orderStatus, fulfillmentChannel, search, dateFrom, dateTo, page, size]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  // Debounced search
  const handleSearchChange = (value) => {
    setSearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(0);
      fetchOrders();
    }, 300);
  };

  // Sort
  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("desc");
    }
    setPage(0);
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ChevronDown size={14} className="text-gray-300" />;
    return sortDir === "asc"
      ? <ChevronUp size={14} className="text-[#C45500]" />
      : <ChevronDown size={14} className="text-[#C45500]" />;
  };

  // Date preset selection
  const handlePresetClick = (presetKey) => {
    if (presetKey === "custom") {
      setCustomFrom(dateFrom);
      setCustomTo(dateTo);
      setShowCustomRange(true);
      return;
    }
    // Preset click — apply immediately, close custom picker, clear forced custom
    const dates = getPresetDates(presetKey);
    if (dates) {
      setDateFrom(dates.from);
      setDateTo(dates.to);
      setPage(0);
      setShowCustomRange(false);
      setForceCustom(false);
    }
  };

  const applyCustomRange = () => {
    if (!isCustomDateValid) return;
    setDateFrom(customFrom);
    setDateTo(customTo);
    setPage(0);
    setShowCustomRange(false);
    setForceCustom(true);
  };

  const cancelCustomRange = () => {
    setShowCustomRange(false);
  };

  const handleSync = async () => {
    // Find authorized accounts for sync
    let accounts = selectedAccountId
      ? sellerChannels.filter(a => a.id === selectedAccountId)
      : sellerChannels;

    if (accounts.length === 0) {
      toast.error("No authorized accounts found for selection");
      return;
    }

    setSyncing(true);
    let totalCreated = 0, totalUpdated = 0;

    for (const account of accounts) {
      try {
        const res = await apiClient.post(`${ENDPOINTS.ORDERS.SYNC}?sellerAccountId=${account.id}&daysBack=${syncDays}`);
        const data = res.data || res;
        totalCreated += data.created || 0;
        totalUpdated += data.updated || 0;
      } catch (err) {
        toast.error(`Sync failed for ${account.sellerName || account.id}`);
      }
    }

    const parts = [];
    if (totalCreated > 0) parts.push(`${totalCreated} new`);
    if (totalUpdated > 0) parts.push(`${totalUpdated} updated`);
    if (parts.length > 0) toast.success(`Synced ${parts.join(", ")} orders`);
    else toast("No new orders found", { icon: "ℹ️" });

    await fetchOrders();
    setSyncing(false);
  };

  const handleExport = () => {
    if (!orders || orders.length === 0) return;
    const headers = ["Order ID", "Platform", "Status", "Purchase Date", "Total", "Currency", "Fulfillment", "Items", "Buyer City"];
    const rows = orders.map(o => [
      o.channelOrderId, o.platform || "", o.orderStatus || "",
      o.purchaseDate || "", o.orderTotal ?? "", o.currencyCode || "",
      o.fulfillmentChannel || "", o.totalQuantity || "",
      o.buyerCity || "",
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `orders-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Export downloaded");
  };

  const handleViewDetail = async (order) => {
    setSelectedOrder(order);
    setShowDetail(true);
    // Fetch order items on demand if not already loaded
    if (!order.orderItems) {
      setLoadingItems(true);
      try {
        const res = await apiClient.get(ENDPOINTS.ORDERS.GET_ITEMS(order.id));
        const data = res.data || res;
        const items = data.orderItems;
        if (items) {
          const updated = { ...order, orderItems: typeof items === 'string' ? items : JSON.stringify(items) };
          setSelectedOrder(updated);
          setPageData(prev => {
            if (!prev?.content) return prev;
            const content = Array.isArray(prev.content) ? prev.content : [];
            return { ...prev, content: content.map(o => o.id === order.id ? updated : o) };
          });
        }
      } catch {
        // Silently fail — items just won't show
      } finally {
        setLoadingItems(false);
      }
    }
  };

  const clearFilters = () => {
    setSearch("");
    setOrderStatus("");
    setFulfillmentChannel("");
    setDateFrom(todayStr());
    setDateTo(todayStr());
    setPaymentStatus("");
    setMinAmount("");
    setMaxAmount("");
    setPage(0);
    setSortBy("purchaseDate");
    setSortDir("desc");
    setShowCustomRange(false);
    setForceCustom(false);
  };

  const hasActiveFilters = search || orderStatus || fulfillmentChannel ||
    activePreset !== "today" || paymentStatus || minAmount || maxAmount;

  const rawContent = pageData?.content;
  const orders = Array.isArray(rawContent) ? rawContent : [];
  const totalElements = pageData?.totalElements || 0;
  const totalPages = Math.ceil(totalElements / size) || 0;

  // Compute stats from current page data
  const stats = useMemo(() => {
    const totalOrders = totalElements;
    let totalRevenue = 0;
    for (const o of orders) {
      totalRevenue += parseFloat(o.orderTotal || 0);
    }
    const averageOrderValue = orders.length > 0 ? totalRevenue / orders.length : 0;
    return { totalOrders, totalRevenue, averageOrderValue };
  }, [orders, totalElements]);

  const startRecord = totalElements > 0 ? page * size + 1 : 0;
  const endRecord = Math.min((page + 1) * size, totalElements);

  // Custom range label for the badge when custom is active
  const customRangeLabel = activePreset === "custom"
    ? `${formatDateShort(dateFrom)} - ${formatDateShort(dateTo)}`
    : null;

  // No seller dropdown needed — seller comes from context

  return (
    <AdminLayout>
      <div className="p-3 sm:p-5 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">Orders & Sales</h1>
            <p className="text-xs text-slate-400 mt-0.5">Synced orders from your marketplace accounts</p>
          </div>
          <div className="flex items-center gap-2">
            {/* Marketplace Selector */}
            <MarketplaceSelector value={selectedAccountId} onChange={setSelectedAccountId} />

            {/* Refresh */}
            <button
              onClick={fetchOrders}
              disabled={loading}
              className="inline-flex items-center px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              title="Refresh data"
            >
              <RefreshCw size={15} className={loading ? "animate-spin" : ""} />
            </button>

            <div className="w-px h-8 bg-gray-200" />

            {/* Sync days + Sync button */}
            <select value={syncDays} onChange={(e) => setSyncDays(e.target.value)}
              className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#f08804]/20">
              <option value="7">Last 7 days</option>
              <option value="14">Last 14 days</option>
              <option value="30">Last 30 days</option>
              <option value="60">Last 60 days</option>
              <option value="90">Last 90 days</option>
            </select>
            <button
              onClick={handleSync}
              disabled={syncing || sellerChannels.length === 0}
              title={sellerChannels.length === 0 ? "No marketplace connected" : "Sync orders from Amazon"}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#232F3E] text-white rounded-lg text-sm font-semibold hover:bg-[#37475A] transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              {syncing ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />}
              {syncing ? "Syncing..." : "Sync Orders"}
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow-sm p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Orders</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{stats.totalOrders.toLocaleString("en-IN")}</p>
              </div>
              <div className="bg-[#232F3E] p-3 rounded-lg">
                <ShoppingCart size={20} className="text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Page Revenue</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{formatINR(stats.totalRevenue)}</p>
              </div>
              <div className="bg-green-500 p-3 rounded-lg">
                <TrendingUp size={20} className="text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Avg Order Value</p>
                <p className="text-2xl font-bold text-gray-800 mt-1">{formatINR(stats.averageOrderValue)}</p>
              </div>
              <div className="bg-[#37475A] p-3 rounded-lg">
                <DollarSign size={20} className="text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* C. Filter Bar */}
        <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
          {/* Row 1: Date preset badges */}
          <div className="flex flex-wrap items-center gap-2">
            <Calendar size={16} className="text-gray-400 mr-1" />
            {DATE_PRESETS.map((preset) => {
              const isActive = preset.key === "custom"
                ? activePreset === "custom" || showCustomRange
                : activePreset === preset.key && !showCustomRange;

              return (
                <button
                  key={preset.key}
                  onClick={() => handlePresetClick(preset.key)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                    isActive
                      ? "bg-[#232F3E] text-white border-[#C45500] shadow-sm"
                      : "bg-white text-gray-600 border-gray-300 hover:border-[#f08804] hover:text-[#C45500]"
                  }`}
                >
                  {preset.key === "custom" && activePreset === "custom" && !showCustomRange
                    ? <>Custom: {customRangeLabel}</>
                    : preset.label}
                </button>
              );
            })}
          </div>

          {/* Custom date range picker panel */}
          {showCustomRange && (
            <div className="flex flex-wrap items-center gap-3 px-3 py-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500 font-medium min-w-[32px]">From</label>
                <input
                  type="date"
                  value={customFrom}
                  max={customTo || undefined}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] bg-white"
                />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs text-gray-500 font-medium min-w-[20px]">To</label>
                <input
                  type="date"
                  value={customTo}
                  min={customFrom || undefined}
                  max={todayStr()}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] bg-white"
                />
              </div>
              {!isCustomDateValid && customFrom && customTo && (
                <span className="text-xs text-red-500">From date must be before To date</span>
              )}
              <div className="flex items-center gap-2 ml-auto">
                <button
                  onClick={cancelCustomRange}
                  className="inline-flex items-center px-3 py-1.5 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={applyCustomRange}
                  disabled={!isCustomDateValid}
                  className="inline-flex items-center px-4 py-1.5 text-sm font-medium text-white bg-[#232F3E] rounded-lg hover:bg-[#37475A] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Check size={14} className="mr-1" />
                  Apply
                </button>
              </div>
            </div>
          )}

          {/* Row 2: Search + dropdown filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Search order ID, SKU, ASIN..."
                className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] w-[260px]"
              />
            </div>

            <select
              value={orderStatus}
              onChange={(e) => { setOrderStatus(e.target.value); setPage(0); }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]"
            >
              <option value="">All Statuses</option>
              {ORDER_STATUS_LIST.map((s) => (
                <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
              ))}
            </select>

            <select
              value={fulfillmentChannel}
              onChange={(e) => { setFulfillmentChannel(e.target.value); setPage(0); }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]"
            >
              <option value="">All Channels</option>
              {FULFILLMENT_CHANNEL_LIST.map((f) => (
                <option key={f} value={f}>{f === "AFN" ? "FBA" : "FBM"}</option>
              ))}
            </select>

            <button
              onClick={() => setShowMoreFilters(!showMoreFilters)}
              className="inline-flex items-center px-3 py-2 text-sm text-gray-600 hover:text-[#C45500] border border-gray-300 rounded-lg hover:border-[#f08804] transition-colors"
            >
              <Filter size={14} className="mr-1" />
              {showMoreFilters ? "Less" : "More"}
            </button>

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="inline-flex items-center px-3 py-2 text-sm text-red-600 hover:text-red-800"
              >
                <X size={14} className="mr-1" />
                Clear Filters
              </button>
            )}
          </div>

          {/* More Filters Row */}
          {showMoreFilters && (
            <div className="flex flex-wrap items-center gap-3 pt-3 border-t">
              <select
                value={paymentStatus}
                onChange={(e) => { setPaymentStatus(e.target.value); setPage(0); }}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804]"
              >
                <option value="">All Payment Statuses</option>
                {PAYMENT_STATUS_LIST.map((s) => (
                  <option key={s} value={s}>{s.replace(/_/g, " ")}</option>
                ))}
              </select>
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  value={minAmount}
                  onChange={(e) => { setMinAmount(e.target.value); setPage(0); }}
                  placeholder="Min Amount"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] w-32"
                />
                <span className="text-gray-400">-</span>
                <input
                  type="number"
                  value={maxAmount}
                  onChange={(e) => { setMaxAmount(e.target.value); setPage(0); }}
                  placeholder="Max Amount"
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20 focus:border-[#f08804] w-32"
                />
              </div>
            </div>
          )}
        </div>

        {/* D. Results Info Row */}
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {totalElements > 0
              ? `Showing ${startRecord}-${endRecord} of ${totalElements.toLocaleString("en-IN")} orders`
              : "No orders found"}
          </span>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExport}
              disabled={orders.length === 0}
              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-40"
            >
              <Download size={14} className="mr-1.5" />
              Export CSV
            </button>
            <select
              value={size}
              onChange={(e) => { setSize(parseInt(e.target.value)); setPage(0); }}
              className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#f08804]/20"
            >
              {PAGE_SIZES.map((s) => (
                <option key={s} value={s}>{s} / page</option>
              ))}
            </select>
          </div>
        </div>

        {/* E. Orders Table */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort("channelOrderId")}>
                    <div className="flex items-center gap-1">Order ID <SortIcon field="channelOrderId" /></div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort("platform")}>
                    <div className="flex items-center gap-1">Platform <SortIcon field="platform" /></div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort("purchaseDate")}>
                    <div className="flex items-center gap-1">Purchased <SortIcon field="purchaseDate" /></div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort("earliestDeliveryDate")}>
                    <div className="flex items-center gap-1">Est. Delivery <SortIcon field="earliestDeliveryDate" /></div>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Items</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort("orderTotal")}>
                    <div className="flex items-center justify-end gap-1">Total <SortIcon field="orderTotal" /></div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort("orderStatus")}>
                    <div className="flex items-center gap-1">Status <SortIcon field="orderStatus" /></div>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none" onClick={() => handleSort("fulfillmentChannel")}>
                    <div className="flex items-center gap-1">Fulfillment <SortIcon field="fulfillmentChannel" /></div>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 9 }).map((_, j) => (
                        <td key={j} className="px-4 py-4">
                          <div className="h-4 bg-gray-200 rounded animate-pulse" style={{ width: `${60 + Math.random() * 40}%` }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : orders.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-16 text-center">
                      <ShoppingCart size={48} className="mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500 text-lg font-medium">No orders found</p>
                      <p className="text-gray-400 text-sm mt-1">
                        {hasActiveFilters ? "Try adjusting your filters or date range." : "No orders recorded for today. Sync to pull latest data."}
                      </p>
                    </td>
                  </tr>
                ) : (
                  orders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => handleViewDetail(order)}>
                      <td className="px-4 py-3 text-sm font-medium text-[#C45500]">{order.channelOrderId}</td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${platformBadge[order.platform] || "bg-gray-100 text-gray-700"}`}>
                          {order.platform}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-sm text-gray-700">{formatDate(order.purchaseDate)}</p>
                          <p className="text-xs text-gray-400">{timeAgo(order.purchaseDate)}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {order.earliestDeliveryDate ? (
                          <div>
                            <p className="text-sm text-gray-700">{formatDate(order.earliestDeliveryDate)}</p>
                            <p className="text-xs text-gray-400">{timeAgo(order.earliestDeliveryDate)}</p>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-300">-</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center text-sm">
                        {order.orderItems ? (
                          <span className="text-gray-700 font-medium">
                            {(() => { try { const p = typeof order.orderItems === 'string' ? JSON.parse(order.orderItems) : order.orderItems; return Array.isArray(p) ? p.length : '-'; } catch { return '-'; } })()}
                          </span>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleViewDetail(order); }}
                            className="inline-flex items-center gap-1 text-[#f08804] hover:text-[#C45500] transition-colors"
                            title="Click to load items"
                          >
                            <Download size={13} />
                            <span className="text-xs font-medium">Load</span>
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right text-sm font-semibold text-gray-800">
                        {order.orderTotal ? (
                          <span title={order.estimatedSource ? `Estimated from ${order.estimatedSource === 'ITEMS' ? 'order items' : 'product catalog'}` : 'Exact amount'}>
                            {order.estimatedSource && <span className="text-amber-500 text-xs mr-0.5">~</span>}
                            {formatINR(order.orderTotal)}
                            {order.estimatedSource && <span className="text-[9px] text-amber-500 ml-1">est.</span>}
                          </span>
                        ) : "-"}
                      </td>
                      <td className="px-4 py-3"><StatusBadge status={order.orderStatus} /></td>
                      <td className="px-4 py-3">
                        {order.fulfillmentChannel ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            order.fulfillmentChannel === "AFN" ? "bg-[#F0F0F0] text-[#37475A]" : "bg-gray-100 text-gray-700"
                          }`}>
                            {order.fulfillmentChannel === "AFN" ? "FBA" : order.fulfillmentChannel === "MFN" ? "FBM" : order.fulfillmentChannel}
                          </span>
                        ) : "-"}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={(e) => { e.stopPropagation(); handleViewDetail(order); }}
                          className="text-gray-400 hover:text-[#C45500] p-1"
                          title="View details"
                        >
                          <Eye size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* F. Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-6 py-3 border-t bg-gray-50">
              <span className="text-sm text-gray-500">Page {page + 1} of {totalPages}</span>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="inline-flex items-center px-3 py-1.5 rounded text-sm text-gray-600 hover:bg-white hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} className="mr-1" />
                  Previous
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i;
                  } else if (page < 3) {
                    pageNum = i;
                  } else if (page > totalPages - 4) {
                    pageNum = totalPages - 5 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`px-3 py-1.5 rounded text-sm ${
                        page === pageNum
                          ? "bg-[#232F3E] text-white"
                          : "text-gray-600 hover:bg-white hover:shadow-sm"
                      }`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}
                <button
                  onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
                  disabled={page >= totalPages - 1}
                  className="inline-flex items-center px-3 py-1.5 rounded text-sm text-gray-600 hover:bg-white hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight size={16} className="ml-1" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Order Detail Side Panel */}
      <OrderDetailPanel
        order={selectedOrder}
        isOpen={showDetail}
        loadingItems={loadingItems}
        onClose={() => { setShowDetail(false); setSelectedOrder(null); setLoadingItems(false); }}
      />
    </AdminLayout>
  );
}
